import {
  CODEX_APP_SERVER_SCHEMA_SNAPSHOT,
  CODEX_SCHEMA_CLIENT_REQUEST_METHODS,
  CODEX_SCHEMA_SERVER_NOTIFICATION_METHODS,
  CODEX_SCHEMA_SERVER_REQUEST_METHODS,
} from "./codex-app-server.js";
import type { CapabilitiesSnapshotPayload } from "./types.js";

/**
 * Honest test-stage baseline: Codex work is backed by app-server; ChatGPT's
 * private consumer UI is not advertised as a callable protocol.
 */
export function createCodexBaselineCapabilities(
  snapshotId: string,
  generatedAt = new Date().toISOString(),
): CapabilitiesSnapshotPayload {
  return {
    snapshotId,
    generatedAt,
    modes: {
      chat: {
        mode: "chat",
        status: "unavailable",
        source: "none",
        reason:
          "No public private-ChatGPT consumer RPC is connected. Enable an explicit API or gateway adapter before advertising chat features.",
      },
      work: {
        mode: "work",
        status: "available",
        source: "codex_app_server",
      },
      video: {
        mode: "video",
        status: "unavailable",
        source: "none",
        reason:
          "No server-side video provider is configured. Never expose provider API keys to the client.",
      },
    },
    features: [
      {
        id: "work.codex_schema_surface",
        status: "degraded",
        source: "codex_app_server",
        operations: [...CODEX_SCHEMA_CLIENT_REQUEST_METHODS],
        reason: "Schema-visible methods require per-method gateway policy; experimental, beta, development, legacy, and destructive operations are not implicitly enabled.",
      },
      {
        id: "chat.private_consumer_ui",
        status: "unavailable",
        source: "none",
        operations: [],
        reason:
          "The Codex app-server schema does not expose the private ChatGPT consumer client surface.",
      },
      {
        id: "video.grok",
        status: "unavailable",
        source: "none",
        operations: [],
        reason:
          "Grok video becomes available only after the gateway confirms a server-side XAI_API_KEY and download signing key.",
      },
      {
        id: "workflow.handoff",
        status: "unavailable",
        source: "none",
        operations: [],
        reason:
          "Cross-mode artifact handoff becomes available only after the authenticated gateway exposes project-scoped materialization and durable handoff storage.",
      },
    ],
    codexAppServer: {
      codexCliVersion: CODEX_APP_SERVER_SCHEMA_SNAPSHOT.codexCliVersion,
      schemaGeneratedWithoutExperimental:
        CODEX_APP_SERVER_SCHEMA_SNAPSHOT.generatedWithoutExperimental,
      clientRequests: [...CODEX_SCHEMA_CLIENT_REQUEST_METHODS],
      serverNotifications: [...CODEX_SCHEMA_SERVER_NOTIFICATION_METHODS],
      reverseRequests: [...CODEX_SCHEMA_SERVER_REQUEST_METHODS],
    },
  };
}
